import { Activity, CalendarCheck, CalendarClock, Clock3, Percent, PhoneCall, Sparkles, Star, TrendingUp, XCircle } from "lucide-react";
import { Area, AreaChart, Bar, BarChart, Cell, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Panel } from "@/components/crm/Badges";

export const KPIS = [
  ["nuevos", "Leads nuevos", Sparkles, "text-sky-600 bg-sky-50"],
  ["activos", "Leads activos", Activity, "text-indigo-600 bg-indigo-50"],
  ["contactados", "Contactados", PhoneCall, "text-blue-600 bg-blue-50"],
  ["interesados", "Interesados", Star, "text-amber-600 bg-amber-50"],
  ["citas_agendadas", "Citas agendadas", CalendarClock, "text-teal-600 bg-teal-50"],
  ["citas_realizadas", "Citas realizadas", CalendarCheck, "text-cyan-700 bg-cyan-50"],
  ["ventas", "Ventas realizadas", TrendingUp, "text-emerald-600 bg-emerald-50"],
  ["perdidos", "Leads perdidos", XCircle, "text-rose-600 bg-rose-50"],
];
const COLORS = ["#1E6FD9", "#14B8A6", "#0B2545", "#60A5FA", "#5EEAD4", "#94A3B8", "#F59E0B", "#A78BFA", "#F472B6"];
const Empty = ({ testid }) => <p className="grid h-40 place-items-center rounded-xl bg-[#F5F7FA] text-sm text-slate-500" data-testid={`${testid}-empty`}>Sin datos para este periodo</p>;

export const KpiGrid = ({ data }) => (
  <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 2xl:grid-cols-5">
    {KPIS.map(([k, label, Icon, tone]) => (
      <div key={k} className="rounded-2xl border border-slate-200/80 bg-white p-4 sm:p-5" data-testid={`kpi-${k}`}>
        <span className={`grid h-9 w-9 place-items-center rounded-xl ${tone}`}><Icon className="h-[18px] w-[18px]" /></span>
        <p className="mt-4 font-display text-3xl font-extrabold tracking-tight" data-testid={`kpi-${k}-value`}>{data ? data.kpis[k] : "–"}</p>
        <p className="mt-1 text-xs font-medium text-slate-500 sm:text-sm">{label}</p>
      </div>
    ))}
    <div className="rounded-2xl border border-[#0B2545] bg-[#0B2545] p-4 text-white sm:p-5" data-testid="kpi-conversion">
      <span className="grid h-9 w-9 place-items-center rounded-xl bg-white/10"><Percent className="h-[18px] w-[18px]" /></span>
      <p className="mt-4 font-display text-3xl font-extrabold tracking-tight" data-testid="kpi-conversion-value">{data ? `${data.conversion.rate}%` : "–"}</p>
      <p className="mt-1 text-xs font-medium text-white/60 sm:text-sm">Conversión · {data ? `${data.conversion.ventas}/${data.conversion.total}` : ""}</p>
    </div>
  </div>
);

export const Chart = ({ title, data, testid }) => {
  const rows = (data || []).filter((d) => d.count > 0);
  return (
    <Panel title={title} testid={testid}>
      {rows.length === 0 ? <Empty testid={testid} /> : (
        <div style={{ height: Math.max(160, rows.length * 42) }}>
          <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 160 }}>
            <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 24, top: 4, bottom: 4 }}>
              <XAxis type="number" allowDecimals={false} hide />
              <YAxis type="category" dataKey="name" width={110} tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(30,111,217,0.05)" }} formatter={(v) => [v, "Leads"]} />
              <Bar dataKey="count" radius={[0, 8, 8, 0]} barSize={22} label={{ position: "right", fontSize: 12, fill: "#0B2545" }}>
                {rows.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </Panel>
  );
};

export const PeriodChart = ({ data }) => (
  <Panel title="Leads por período" testid="chart-by-period" className="xl:col-span-2">
    {!data?.length ? <Empty testid="chart-by-period" /> : (
      <div className="h-56">
        <ResponsiveContainer width="100%" height="100%" initialDimension={{ width: 320, height: 200 }}>
          <AreaChart data={data} margin={{ left: -20, right: 8, top: 8 }}>
            <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(d) => d.slice(5)} />
            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#64748b" }} />
            <Tooltip formatter={(v) => [v, "Leads"]} />
            <Area type="monotone" dataKey="count" stroke="#1E6FD9" fill="#1E6FD9" fillOpacity={0.12} strokeWidth={2} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    )}
  </Panel>
);

export const Funnel = ({ data }) => {
  const max = Math.max(1, ...(data || []).map((s) => s.count));
  return (
    <Panel title="Embudo de conversión" testid="dashboard-funnel">
      <div className="space-y-2.5">
        {(data || []).map((s, i) => (
          <div key={s.stage} data-testid={`funnel-${i}`}>
            <div className="flex justify-between text-xs"><span className="font-medium text-slate-600">{s.stage}</span>
              <span><b className="text-[#0B2545]">{s.count}</b>{s.pct_prev !== null && <span className="text-slate-400"> · {s.pct_prev}%</span>}</span></div>
            <div className="mt-1 h-2.5 rounded-full bg-[#F5F7FA]"><div className="h-2.5 rounded-full bg-gradient-to-r from-[#1E6FD9] to-[#14B8A6]" style={{ width: `${(s.count / max) * 100}%` }} /></div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

const mins = (m) => (m === null || m === undefined ? "—" : m < 60 ? `${Math.round(m)} min` : `${(m / 60).toFixed(1)} h`);

export const FirstResponse = ({ frt }) => (
  <Panel title="Tiempo de primera respuesta" testid="dashboard-frt">
    <div className="flex items-end gap-6">
      <div><p className="font-display text-3xl font-extrabold" data-testid="frt-avg">{mins(frt?.avg_minutes)}</p><p className="text-xs text-slate-500">Promedio ({frt?.responded || 0} respondidos)</p></div>
      <div><p className="font-display text-2xl font-bold text-amber-600" data-testid="frt-sin-respuesta">{frt?.sin_respuesta ?? 0}</p><p className="text-xs text-slate-500">Sin respuesta</p></div>
      <Clock3 className="ml-auto h-8 w-8 text-slate-200" />
    </div>
    {frt?.by_seller && (
      <ul className="mt-4 divide-y divide-slate-100 text-sm" data-testid="frt-by-seller">
        {frt.by_seller.map((s) => (
          <li key={s.name} className="flex justify-between py-2"><span className="text-slate-600">{s.name}</span>
            <span><b>{mins(s.avg_minutes)}</b>{s.sin_respuesta > 0 && <span className="text-xs text-amber-600"> · {s.sin_respuesta} sin respuesta</span>}</span></li>
        ))}
      </ul>
    )}
  </Panel>
);
